import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  SmartFormInterFace,
  SmartSoftButton,
  SmartTable,
  SmartTableNewInterface,
} from "soft_digi";
import { useSiteContext } from "../../contexts/SiteProvider";
import { post } from "../../services/smartApiService";
import MSG from "../../services/MsgService";

interface ImportExcelLayoutProps {
  loadTableData: () => void;
  uploadUrl: string;
  templateHeaders: string[];
  templateSampleData?: string[][];
  templateFileName?: string;
  description?: string;
  columns: SmartTableNewInterface.SmartTableNewColumnConfig[];
  filterFields?: SmartFormInterFace.SmartFormElementProps[];
}

const ImportExcelLayout: React.FC<ImportExcelLayoutProps> = ({
  loadTableData,
  uploadUrl,
  templateHeaders,
  templateSampleData = [],
  templateFileName = "template.xlsx",
  description,
  columns,
  filterFields,
}) => {
  const { closeModal } = useSiteContext();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    name: string;
    content: string;
    type: string;
  } | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [successCount, setSuccessCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([templateHeaders, ...templateSampleData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, templateFileName);
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      const content = result.includes(",") ? result.split(",")[1] : result;
      setSelectedFile({ name: file.name, content, type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const countExcelRows = (content: string): number => {
    try {
      const wb = XLSX.read(content, { type: "base64" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      return XLSX.utils.sheet_to_json(ws).length;
    } catch {
      return 0;
    }
  };

  const handleImport = () => {
    if (!selectedFile) return;
    const totalRows = countExcelRows(selectedFile.content);
    const subscription = post(
      uploadUrl,
      { excel: selectedFile },
      { loadingMsg: MSG.LOADING.IMPORT }
    ).subscribe((response) => {
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setData(rows);
      const errorCount = rows.filter((r: any) => Number(r.status) === 10).length;
      const sc =
        response.data?.success_count ??
        response.data?.success ??
        Math.max(0, totalRows - errorCount);
      setSuccessCount(sc);
      loadTableData();
    });
    return () => subscription.unsubscribe();
  };

  const tableTop: SmartTableNewInterface.SmartTableNewTopProps[] = [
    { type: "CUSTOM", widthClass: "is-10", custom: <p className="is-size-4"></p> },
    {
      type: "BUTTONS",
      widthClass: "is-2",
      align: "CENTER",
      buttons: [{ type: "FILTER" }],
    },
  ];

  const resolvedFilterFields: SmartFormInterFace.SmartFormElementProps[] =
    filterFields ?? [
      {
        type: "SELECT_BOX",
        width: "12",
        name: "status",
        element: {
          label: "Status",
          options: [
            { value: "5", label: "Success" },
            { value: "10", label: "Error" },
          ],
        },
      },
    ];

  if (data.length > 0) {
    return (
      <>
        <div className="columns has-text-centered mt-2 mb-3">
          <div className="column">
            <p className="has-text-weight-bold is-uppercase is-size-7 has-text-grey">
              Imported
            </p>
            <p className="is-size-3 has-text-success has-text-weight-bold">
              {successCount}
            </p>
          </div>
          <div className="column">
            <p className="has-text-weight-bold is-uppercase is-size-7 has-text-grey">
              Errors
            </p>
            <p className="is-size-3 has-text-danger has-text-weight-bold">
              {data.filter((item: any) => Number(item.status) === 10).length}
            </p>
          </div>
        </div>
        <SmartTable
          columns={columns}
          data={data}
          tableTop={tableTop}
          filterFields={resolvedFilterFields}
          paginationProps={{ pageSize: 10 }}
          tableProps={{
            className: "is-hoverable is-bordered is-striped",
            isResponsive: true,
          }}
        />
        <div className="has-text-centered">
          <SmartSoftButton
            label="Close"
            classList={["button", "mt-4", "smart-third-button"]}
            onClick={closeModal}
          />
        </div>
      </>
    );
  }

  return (
    <div>
      {description && (
        <p className="mb-4 has-text-grey-dark">{description}</p>
      )}

      <button className="button is-warning mb-4" onClick={downloadTemplate}>
        <span className="icon">
          <i className="fa fa-download"></i>
        </span>
        <span>Download Template</span>
      </button>

      <div
        className="notification is-info is-light mb-4"
        style={{ padding: "1rem 1.25rem" }}
      >
        <p className="has-text-weight-bold mb-2">Supported formats:</p>
        <ul
          style={{
            listStyleType: "disc",
            paddingLeft: "1.25rem",
            marginBottom: "0.75rem",
          }}
        >
          <li>
            <strong>Excel (.xlsx)</strong> — recommended format
          </li>
          <li>Excel 97-2003 (.xls)</li>
          <li>CSV (.csv) — comma-separated values</li>
        </ul>
        <p className="has-text-weight-bold mb-2">How it works:</p>
        <ul style={{ listStyleType: "disc", paddingLeft: "1.25rem" }}>
          <li>Download the template and fill in your data</li>
          <li>
            Column headers <strong>must match</strong> the template —
            unmatched columns are skipped
          </li>
          <li>
            Upload the filled file and click <strong>Import</strong>
          </li>
        </ul>
      </div>

      <div
        style={{
          border: `2px dashed ${
            isDragging ? "#3273dc" : selectedFile ? "#48c774" : "#dbdbdb"
          }`,
          borderRadius: "8px",
          padding: "2.5rem 1rem",
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragging
            ? "#f0f5ff"
            : selectedFile
            ? "#f0fdf4"
            : "transparent",
          transition: "all 0.2s ease",
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <i
          className="fa fa-cloud-upload"
          style={{
            fontSize: "2.5rem",
            display: "block",
            marginBottom: "0.75rem",
            color: selectedFile ? "#48c774" : "#b5b5b5",
          }}
        ></i>
        {selectedFile ? (
          <>
            <p className="has-text-success has-text-weight-bold">
              {selectedFile.name}
            </p>
            <p className="has-text-grey is-size-7 mt-1">
              File selected — click Import to proceed
            </p>
          </>
        ) : (
          <>
            <p className="has-text-weight-semibold">
              Drag &amp; drop your Excel file here
            </p>
            <p className="has-text-grey is-size-7 mt-1">
              or click to browse (.xlsx, .xls, .csv)
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) processFile(f);
            e.target.value = "";
          }}
        />
      </div>

      <div className="has-text-right mt-4">
        <SmartSoftButton
          label="Cancel"
          classList={["button", "mr-3", "smart-third-button"]}
          onClick={closeModal}
        />
        <SmartSoftButton
          label="Import"
          classList={[
            "button",
            "is-success",
            ...(selectedFile ? [] : ["is-light"]),
          ]}
          onClick={handleImport}
        />
      </div>
    </div>
  );
};

export default ImportExcelLayout;
